import React from "react";
import classNames from "classnames";
import { useRouter } from "next/router";

import { Action, Category } from "constants/events";
import { useEventTracker } from "hooks/useEventTracker";
import useFilters from "hooks/useFilters";

import Dropdown from "components/botanic/Dropdown";
import DropdownItem from "components/botanic/Dropdown/DropdownItem";

const SORT_TEXT: { [key: string]: string } = {
  deals: "Deals (% savings high to low)",
  priceasc: "Price Low-High",
  pricedesc: "Price High-Low",
  thc_contentasc: "THC Low-High",
  thc_contentdesc: "THC High-Low",
};

const ShopSort: React.FC<{ showEmptyState?: boolean }> = ({
  showEmptyState,
}) => {
  const {
    query: { sort, sort_direction, deal_active, shopCategory, showSimilar },
  } = useRouter();
  const { publishEvent } = useEventTracker();
  const { addFilter } = useFilters();

  const showSimilarStrains =
    shopCategory === "strain" &&
    (showSimilar === "on" || showSimilar === "menu_items_only");

  const getSortSelection = () => {
    if (deal_active) {
      return SORT_TEXT["deals"];
    }
    return (
      SORT_TEXT[
        `${sort ? String(sort) : ""}${
          sort_direction ? String(sort_direction) : ""
        }`
      ] || "Recommended"
    );
  };

  const getSortText = (value?: string, direction?: string) =>
    SORT_TEXT[`${value}${direction || ""}`] || "Recommended";

  const handleSelectSortOption: React.MouseEventHandler<HTMLButtonElement> = (
    e,
  ) => {
    const { value, direction } = e.currentTarget.dataset;

    if (value === "deals") {
      addFilter({
        deal_active: "true",
        sort: undefined,
        sort_direction: undefined,
      });
    } else {
      addFilter({
        deal_active: undefined,
        sort: value,
        sort_direction: direction,
      });
    }

    publishEvent({
      action: Action.click,
      category: Category.resultsSorted,
      label: getSortText(value, direction).toLowerCase(),
    });
  };

  // Recommended is the default when no value is applied
  const sortOptions = showSimilarStrains
    ? [{ value: "deals" }, {}]
    : [
        { value: "deals" },
        { direction: "asc", value: "price" },
        { direction: "desc", value: "price" },
        { direction: "asc", value: "thc_content" },
        { direction: "desc", value: "thc_content" },
        {},
      ];

  return (
    <div
      className={classNames(
        "items-center flex text-right text-xs justify-end",
        showEmptyState ? "hidden" : "",
      )}
    >
      <div>Sort by</div>
      <div className="pl-xs">
        <Dropdown
          className="items-center flex text-right text-xs"
          selectedItem={getSortSelection()}
          data-testid="sort-dropdown"
        >
          {sortOptions.map(({ value, direction }) => {
            const displayText = getSortText(value, direction);

            return (
              <DropdownItem
                key={`${value || "rec"}-${direction}`}
                onClick={handleSelectSortOption}
                data-value={value}
                data-direction={direction}
              >
                {displayText}
              </DropdownItem>
            );
          })}
        </Dropdown>
      </div>
    </div>
  );
};

export default ShopSort;
